<template>
    <div class="admin-upload-page">
        <!-- 頂部導航 -->
        <div class="top-nav">
            <div class="nav-left">
                <button @click="goBack" class="back-btn">
                    {{ $t('aiprompt.adminUpload.backButton') }}
                </button>
                <h1 class="title">{{ $t('aiprompt.adminUpload.title') }}</h1>
            </div>
            <div class="nav-right">
                <div class="user-info">
                    <span class="admin-badge">{{ $t('aiprompt.adminUpload.adminBadge') }}</span>
                    <span class="user-id">{{ currentUserId }}</span>
                </div>
            </div>
        </div>

        <!-- 主要上傳區域 -->
        <div class="upload-container">
            <div class="upload-card">
                <div class="upload-header">
                    <div class="upload-icon">📁</div>
                    <h2>{{ $t('aiprompt.adminUpload.uploadTitle') }}</h2>
                    <p class="upload-subtitle">{{ $t('aiprompt.adminUpload.uploadSubtitle') }}</p>
                </div>

                <!-- 當前檔案下載連結 -->
                <div class="current-file-section">
                    <div class="current-file-info">
                        <span class="file-label">{{ $t('aiprompt.adminUpload.currentFileLabel') }}</span>
                        <a 
                            :href="currentFileUrl" 
                            target="_blank"
                            class="download-link"
                            @click="trackDownload"
                        >
            HT_AIprompt.xlsx
                        </a>
                        <span class="file-note">{{ $t('aiprompt.adminUpload.downloadCurrentFile') }}</span>
                    </div>
                    <p class="upload-notice">
{{ $t('aiprompt.adminUpload.uploadWarning') }}
                    </p>
                </div>

                <!-- 拖放上傳區域 -->
                <div 
                    class="drop-zone" 
                    :class="{ 'drag-over': isDragOver, 'uploading': isUploading }"
                    @drop="handleDrop"
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @dragenter.prevent
                >
                    <div v-if="!selectedFile" class="drop-zone-content">
                        <div class="drop-icon">⬆️</div>
                        <p class="drop-text">{{ $t('aiprompt.adminUpload.dropZoneText') }}</p>
                        <button class="upload-btn" @click="triggerFileInput" :disabled="isUploading">
                            {{ $t('aiprompt.adminUpload.selectFile') }}
                        </button>
                        <p class="file-info">{{ $t('aiprompt.adminUpload.supportedFormat') }}</p>
                    </div>
                    
                    <!-- 選中檔案顯示 -->
                    <div v-else class="selected-file">
                        <div class="file-icon">📄</div>
                        <div class="file-details">
                            <h4>{{ selectedFile.name }}</h4>
                            <p>{{ formatFileSize(selectedFile.size) }}</p>
                            <p class="file-type">{{ getFileType(selectedFile.name) }}</p>
                        </div>
                        <button v-if="!isUploading" @click="clearSelection" class="remove-btn">✕</button>
                    </div>
                    
                    <!-- 上傳進度 -->
                    <div v-if="isUploading" class="upload-progress">
                        <div class="progress-bar">
                            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                        </div>
                        <p>{{ $t('aiprompt.adminUpload.uploading') }} {{ uploadProgress }}%</p>
                    </div>
                </div>

                <!-- 隱藏的檔案輸入 -->
                <input 
                    ref="fileInput"
                    type="file"
                    accept=".xlsx"
                    @change="handleFileSelect"
                    style="display: none"
                >

                <!-- 上傳按鈕 -->
                <div v-if="selectedFile && !isUploading" class="upload-actions">
                    <button @click="uploadFile" class="confirm-upload-btn">
                        {{ $t('aiprompt.adminUpload.startUpload') }}
                    </button>
                    <button @click="clearSelection" class="cancel-btn">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                </div>

                <!-- 錯誤訊息 -->
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                </div>

                <!-- 成功訊息 -->
                <div v-if="successMessage" class="success-message">
                    {{ successMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import jwt_decode from 'jwt-decode'
import { BlobServiceClient } from '@azure/storage-blob'
import * as XLSX from 'xlsx'
import api from '@/api'
import aipromptAPI from '@/api/aipromptAPI'
import profile from '@/api/profile'

const router = useRouter()
const { t } = useI18n()

// 取得當前環境配置
const serverLocation = api.main.getServerLocation()
const blobDomainUrl = profile.blobDomain[serverLocation]

// 響應式狀態
const currentUserId = ref('')
const selectedFile = ref(null)
const isDragOver = ref(false)
const isUploading = ref(false)
const uploadProgress = ref(0)
const errorMessage = ref('')
const successMessage = ref('')
const fileInput = ref(null)
const currentFileUrl = ref('')

// 生成當前檔案的下載 URL
const generateCurrentFileUrl = async () => {
    try {
        // 獲取 SAS Token
        const sasResponse = await aipromptAPI.getAIPromptSasToken()
        
        if (sasResponse.token) {
            // 建構下載 URL
            const fileName = 'HT_AIprompt.xlsx'
            currentFileUrl.value = `${blobDomainUrl}/${sasResponse.containerName}/${fileName}?${sasResponse.token}`
        }
    } catch (error) {
        console.error(t('aiprompt.errors.generateDownloadUrlFailed') + ':', error)
        // 如果無法生成 SAS URL，使用一般的檔案 URL（可能需要權限）
        currentFileUrl.value = `${blobDomainUrl}/ht-ai-prompts/HT_AIprompt.xlsx`
    }
}

// 追蹤下載行為
const trackDownload = () => {
    console.log('管理員下載了 HT_AIprompt.xlsx')
    // 可以在這裡添加下載統計或日誌記錄
}

// 返回上一頁
const goBack = () => {
    router.push('/ai-prompt-home')
}

// 觸發檔案選擇
const triggerFileInput = () => {
    fileInput.value.click()
}

// 處理檔案選擇
const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        validateAndSetFile(file)
    }
}

// 處理拖放
const handleDrop = (event) => {
    event.preventDefault()
    isDragOver.value = false
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
        validateAndSetFile(files[0])
    }
}

// 驗證並設置檔案
const validateAndSetFile = (file) => {
    errorMessage.value = ''
    successMessage.value = ''
    
    // 檢查檔案類型
    const fileName = file.name.toLowerCase()
    
    if (!fileName.endsWith('.xlsx')) {
        errorMessage.value = t('aiprompt.errors.onlyXlsxSupported')
        return
    }
    
    // 檢查檔案大小 (限制 10MB)
    if (file.size > 10 * 1024 * 1024) {
        errorMessage.value = t('aiprompt.errors.fileTooLarge')
        return
    }
    
    selectedFile.value = file
}

// 清除選擇
const clearSelection = () => {
    selectedFile.value = null
    errorMessage.value = ''
    successMessage.value = ''
    uploadProgress.value = 0
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

// 格式化檔案大小
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 獲取檔案類型
const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    return ext.toUpperCase() + ' 檔案'
}

// Excel 檔案解析函數
const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                
                console.log('Excel工作表:', workbook.SheetNames)
                
                // 解析所有工作表
                const allSheets = {}
                workbook.SheetNames.forEach(sheetName => {
                    const worksheet = workbook.Sheets[sheetName]
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
                    allSheets[sheetName] = jsonData
                })
                
                resolve(allSheets)
            } catch (error) {
                reject(new Error(t('aiprompt.errors.excelParseFailed', { error: error.message })))
            }
        }
        reader.onerror = () => reject(new Error(t('aiprompt.errors.fileReadFailed')))
        reader.readAsArrayBuffer(file)
    })
}

// 轉換Excel資料為多語系格式
const convertExcelToMultiLanguage = (sheetsData) => {
    const languages = {}
    
    Object.keys(sheetsData).forEach(sheetName => {
        const rows = sheetsData[sheetName]
        if (rows.length === 0) return
        
        // 從工作表名稱推斷語言代碼
        let languageCode = 'unknown'
        if (sheetName.includes('zh_TW')) {
            languageCode = 'zh_TW'
        } else if (sheetName.includes('zh_CN')) {
            languageCode = 'zh_CN'
        } else if (sheetName.includes('en')) {
            languageCode = 'en'
        } else {
            // 如果無法識別，使用工作表名稱作為語言代碼
            languageCode = sheetName.replace(/[^a-zA-Z_]/g, '_')
        }
        
        // 獲取標題行
        const headers = rows[0] || []
        console.log(`工作表 ${sheetName} 標題行:`, headers)
        
        // 轉換資料 - 將每一行作為一個條目
        languages[languageCode] = {}
        
        for (let i = 1; i < rows.length; i++) {
            const row = rows[i] || []
            if (row.length === 0) continue
            
            // 使用編號作為key，如果沒有編號就用行號
            const id = row[0] || `row_${i}`
            
            // 建立該條目的完整資料
            const entry = {}
            headers.forEach((header, index) => {
                if (header && row[index] !== undefined && row[index] !== '') {
                    // 清理欄位名稱作為key
                    const fieldKey = header.toString().replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '_')
                    entry[fieldKey] = row[index].toString()
                }
            })
            
            if (Object.keys(entry).length > 0) {
                languages[languageCode][id.toString()] = entry
            }
        }
    })
    
    return languages
}


// Azure Blob 上傳函數（參考 HTwbCard 模式）
const uploadToAzureBlob = async (file, filePath) => {
    // 檔案大小限制檢查 (10MB)
    const maxFileSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxFileSize) {
        throw new Error(t('aiprompt.errors.fileTooLargeWithSize', { size: formatFileSize(file.size) }))
    }
    
    const sasToken = sessionStorage.getItem("aiprompt_sas_token")
    if (!sasToken) {
        throw new Error(t('aiprompt.errors.sasTokenExpired'))
    }

    try {
        const containerName = "ht-ai-prompts"
        const blobServiceUrl = `${blobDomainUrl}?${sasToken}`
        const blobServiceClient = new BlobServiceClient(blobServiceUrl)
        const containerClient = blobServiceClient.getContainerClient(containerName)
        const blobClient = containerClient.getBlobClient(filePath)
        const blockBlobClient = blobClient.getBlockBlobClient()
        
        // 設定上傳選項
        const uploadOptions = {
            blobHTTPHeaders: {
                blobContentType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            onProgress: (progress) => {
                if (progress.loadedBytes && file.size > 0) {
                    uploadProgress.value = Math.round((progress.loadedBytes / file.size) * 100)
                }
            }
        }
        
        // 執行上傳
        const response = await blockBlobClient.uploadData(file, uploadOptions)
        
        return response
        
    } catch (error) {
        console.error('Azure Blob 上傳錯誤:', error)
        if (error.statusCode === 403) {
            throw new Error(t('aiprompt.errors.uploadPermissionInsufficient'))
        } else if (error.statusCode === 404) {
            throw new Error(t('aiprompt.errors.containerNotExists'))
        } else {
            throw new Error(t('aiprompt.errors.uploadFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
        }
    }
}

// 上傳檔案
const uploadFile = async () => {
    if (!selectedFile.value) return
    
    isUploading.value = true
    uploadProgress.value = 0
    errorMessage.value = ''
    successMessage.value = ''
    
    try {
        uploadProgress.value = 10
        
        // 解析Excel檔案以驗證格式
        const sheetsData = await parseExcelFile(selectedFile.value)
        // console.log('Excel解析結果:', sheetsData)
        
        uploadProgress.value = 30
        
        // 轉換為多語系格式以驗證資料
        const multiLanguageData = convertExcelToMultiLanguage(sheetsData)
        console.log('多語系資料:', multiLanguageData)
        
        // 檢查是否有資料
        if (Object.keys(multiLanguageData).length === 0) {
            throw new Error(t('aiprompt.errors.noValidLanguageData'))
        }
        
        uploadProgress.value = 50
        
        // 先獲取 SAS Token
        const sasResponse = await aipromptAPI.getAIPromptSasToken()
        
        if (!sasResponse.token) {
            throw new Error(t('aiprompt.errors.cannotGetUploadPermission'))
        }
        
        // 儲存 SAS Token 到 sessionStorage
        sessionStorage.setItem("aiprompt_sas_token", sasResponse.token)
        
        uploadProgress.value = 70
        
        // 直接上傳Excel檔案
        const filePath = 'HT_AIprompt.xlsx'
        await uploadToAzureBlob(selectedFile.value, filePath)
        
        uploadProgress.value = 100
        successMessage.value = t('aiprompt.adminUpload.uploadSuccess')
        Message.success(t('aiprompt.adminUpload.uploadSuccess'))
        
        // 顯示解析資訊
        // console.log('檢測到的語言:', Object.keys(multiLanguageData))
        // console.log('Excel工作表:', Object.keys(sheetsData))
        
        // 重新生成下載 URL
        await generateCurrentFileUrl()
        
        // 3秒後清除狀態
        setTimeout(() => {
            clearSelection()
            successMessage.value = ''
        }, 3000)
        
    } catch (error) {
        console.error('上傳失敗:', error)
        errorMessage.value = error.message || t('aiprompt.errors.fileUploadFailed')
        uploadProgress.value = 0
    } finally {
        isUploading.value = false
    }
}

// 初始化用戶資訊
const initializeUserInfo = () => {
    const idToken = localStorage.getItem('id_token')
    
    if (!idToken) {
        Message.error(t('aiprompt.adminLogin.noLoginInfo'))
        router.push('/ai-prompt-home')
        return
    }
    
    try {
        const decoded = jwt_decode(idToken)
        currentUserId.value = decoded.sub || '未知'
        
        // 確認是否為管理員用戶
        if (decoded.sub !== '1595321354') {
            Message.error(t('aiprompt.adminLogin.noAdminPermission'))
            router.push('/ai-prompt-home')
            return
        }
        
        // 檢查管理員驗證狀態
        const adminVerified = sessionStorage.getItem('adminVerified')
        if (!adminVerified) {
            Message.error(t('aiprompt.errors.completeAdminVerificationFirst'))
            router.push('/admin-login')
            return
        }
        
    } catch (error) {
        console.error(t('aiprompt.errors.tokenParseFailed') + ':', error)
        Message.error(t('aiprompt.errors.loginInfoInvalid'))
        router.push('/ai-prompt-home')
    }
}

// 組件掛載時初始化
onMounted(async () => {
    initializeUserInfo()
    await generateCurrentFileUrl()
})
</script>

<style lang="less" scoped>
@import "../assets/HTwbCardCSS/HTwbCardVal.less";

.admin-upload-page {
    min-height: 100vh;
    background: #f5f5f5;
    
    .top-nav {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 2rem;
        background: white;
        border-bottom: 1px solid #e0e0e0;
        height: @navbar-height;
        
        .nav-left {
            display: flex;
            align-items: center;
            gap: 1rem;
            
            .back-btn {
                background: #f0f0f0;
                border: 1px solid #d0d0d0;
                color: #333;
                padding: 5px;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.3s ease;
                
                &:hover {
                    background: #e0e0e0;
                }
            }
            
            .title {
                color: #333;
                font-size: 1rem;
                font-weight: 600;
                margin: 0;
            }
        }
        
        .nav-right {
            .user-info {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                
                .admin-badge {
                    background: @primary-color;
                    color: white;
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .user-id {
                    color: #666;
                    font-family: 'Courier New', monospace;
                    font-size: 0.9rem;
                }
            }
        }
    }
}

.upload-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - @navbar-height);
    padding: 2rem;
    
    .upload-card {
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        padding: 2rem;
        width: 100%;
        max-width: 600px;
        
        .upload-header {
            text-align: center;
            margin-bottom: 1.5rem;
            
            .upload-icon {
                font-size: 3rem;
                margin-bottom: 1rem;
            }
            
            h2 {
                color: @text-color;
                font-size: 1.5rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
            }
            
            .upload-subtitle {
                color: @text-color-gray;
                margin: 0;
            }
        }
        
        .current-file-section {
            background: #f8f9ff;
            border: 1px solid #e3e8ff;
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 2rem;
            
            .current-file-info {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                margin-bottom: 0.5rem;
                
                .file-label {
                    font-weight: 600;
                    color: @text-color;
                }
                
                .download-link {
                    color: @primary-color;
                    text-decoration: none;
                    font-weight: 600;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    
                    &:hover {
                        background: @primary-color;
                        color: white;
                        text-decoration: none;
                    }
                }
                
                .file-note {
                    color: @text-color-gray;
                    font-size: 0.85rem;
                }
            }
            
            .upload-notice {
                color: #d97706;
                font-size: 0.9rem;
                margin: 0;
                
                strong {
                    color: @danger-color;
                }
            }
        }
        
        .drop-zone {
            border: 2px dashed #d0d0d0;
            border-radius: 8px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s ease;
            min-height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
            
            &.drag-over {
                border-color: @primary-color;
                background: lighten(@primary-color, 45%);
            }
            
            &.uploading {
                border-color: @success-color;
                background: lighten(@success-color, 45%);
            }
            
            .drop-zone-content {
                .drop-icon {
                    font-size: 2rem;
                    margin-bottom: 1rem;
                }
                
                .drop-text {
                    color: @text-color-gray;
                    margin-bottom: 1rem;
                    font-size: 1rem;
                }
                
                .upload-btn {
                    background: @primary-color;
                    color: white;
                    border: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    
                    &:hover:not(:disabled) {
                        background: @primary-color-hover;
                    }
                    
                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                }
                
                .file-info {
                    color: @text-color-gray;
                    font-size: 0.8rem;
                    margin-top: 0.5rem;
                }
            }
            
            .selected-file {
                display: flex;
                align-items: center;
                gap: 1rem;
                width: 100%;
                
                .file-icon {
                    font-size: 2rem;
                }
                
                .file-details {
                    flex: 1;
                    text-align: left;
                    
                    h4 {
                        margin: 0 0 0.25rem 0;
                        color: @text-color;
                        font-weight: 600;
                    }
                    
                    p {
                        margin: 0.1rem 0;
                        color: @text-color-gray;
                        font-size: 0.9rem;
                    }
                    
                    .file-type {
                        font-weight: 600;
                        color: @primary-color;
                    }
                }
                
                .remove-btn {
                    background: none;
                    border: none;
                    color: @text-color-gray;
                    cursor: pointer;
                    font-size: 1.2rem;
                    padding: 0.5rem;
                    border-radius: 50%;
                    
                    &:hover {
                        background: #f0f0f0;
                        color: @danger-color;
                    }
                }
            }
            
            .upload-progress {
                width: 100%;
                
                .progress-bar {
                    width: 100%;
                    height: 8px;
                    background: #f0f0f0;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-bottom: 1rem;
                    
                    .progress-fill {
                        height: 100%;
                        background: @success-color;
                        transition: width 0.3s ease;
                    }
                }
                
                p {
                    margin: 0;
                    color: @success-color;
                    font-weight: 600;
                }
            }
        }
        
        .upload-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 1.5rem;
            
            .confirm-upload-btn {
                background: @success-color;
                color: white;
                border: none;
                padding: 0.75rem 2rem;
                border-radius: 6px;
                cursor: pointer;
                font-weight: 600;
                transition: all 0.3s ease;
                
                &:hover {
                    background: darken(@success-color, 10%);
                }
            }
            
            .cancel-btn {
                background: transparent;
                color: @text-color-gray;
                border: 1px solid #d0d0d0;
                padding: 0.75rem 2rem;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.3s ease;
                
                &:hover {
                    border-color: @text-color-gray;
                    color: @text-color;
                }
            }
        }
        
        .error-message {
            background: #f8d7da;
            color: @danger-color;
            padding: 1rem;
            border-radius: 6px;
            margin-top: 1rem;
            font-weight: 600;
        }
        
        .success-message {
            background: #d4edda;
            color: @success-color;
            padding: 1rem;
            border-radius: 6px;
            margin-top: 1rem;
            font-weight: 600;
        }
    }
}

// 響應式設計
@media (max-width: 768px) {
    .upload-container {
        padding: 1rem;
        
        .upload-card {
            padding: 1.5rem;
        }
    }
    
    .admin-upload-page .top-nav {
        padding: 0 1rem;
        
        .nav-left .title {
            font-size: 0.9rem;
        }
        
        .nav-right .user-info {
            .admin-badge {
                display: none;
            }
        }
    }
}
</style>